import { useLayoutEffect, useMemo, useRef } from "react";
import { SELECTION_COLUMN_WIDTH_PX } from "../useModelTaskTableStyles";

// Roboto Condensed at the table's 14px body size averages out to roughly this
// many pixels per character. A character-count estimate (rather than a canvas
// measureText call) keeps this dependency-free and gives identical results
// under jsdom in tests as it does in a real browser.
const AVG_CHAR_WIDTH_PX = 6.5;
const HEADER_BOLD_FACTOR = 1.1;
// Horizontal cell padding plus the sort-arrow / tooltip icon gutter.
const CELL_PADDING_PX = 40;
const MIN_COLUMN_WIDTH_PX = 80;
// Matches the flat 220px cap every body cell used to carry before per-column
// widths existed. The expanded row's Details tabs span the full table width
// (colSpan), so letting columns grow past what the app was actually designed
// around stretches that panel wider than it should be.
const MAX_COLUMN_WIDTH_PX = 220;
// The first loaded page is enough to gauge how long this tenant's values
// typically run; scanning every loaded row would only move the estimate for
// outliers while costing more on every recompute.
const SAMPLE_SIZE = 50;
// Column widths round to whole pixels but the container's own layout math
// stays fractional, so a sum that lands within a pixel of the container can
// still overflow it by a hair - enough for overflow-x to show a scrollbar
// even though nothing looks clipped. This margin keeps the fit comfortably
// inside the container instead of flush against it.
const LAYOUT_SAFETY_MARGIN_PX = 4;
// The last column in `columns` always also renders ModelTaskRowActions (the
// dot-menu button) alongside its own text - see ModelTaskRowContent.js's
// `arr.length === i + 1` check. That button is ~44px on its own (a 4px-wide
// icon plus 20px padding either side) before its 3px margin, per
// TableStyle.js's DotMenu/TableMenuButton. Measuring only the cell's text
// value, as every other column does, would let that column shrink to the
// bare MIN_COLUMN_WIDTH_PX floor under container pressure - which is less
// than the button alone needs, squeezing out the text entirely and leaving
// no room to be sure the button itself still renders cleanly.
const ACTIONS_BUTTON_WIDTH_PX = 48;

const stripHtml = (value) => String(value).replace(/<[^>]+>/g, "");

const estimatedTextWidth = (value, { bold = false } = {}) => {
	if (value === null || value === undefined || value === "") return 0;
	const length = stripHtml(value).length;
	return length * AVG_CHAR_WIDTH_PX * (bold ? HEADER_BOLD_FACTOR : 1);
};

const clamp = (value, min = MIN_COLUMN_WIDTH_PX) =>
	Math.min(MAX_COLUMN_WIDTH_PX, Math.max(min, Math.round(value)));

// Scales `items` down to fit `budget`, respecting each one's own minimum. A
// single flat scale can't do this alone: a column already near the minimum
// would get scaled below it, so its "unusable" shrink allowance has to be
// handed back to the columns that can still give up space - the same
// give-back a flex-shrink layout does for children pinned at min-width.
const fitToBudget = (items, budget) => {
	const finalWidths = new Map();
	let remaining = items;
	let available = budget;

	while (remaining.length > 0) {
		const total = remaining.reduce((sum, item) => sum + item.width, 0);
		if (total <= available) {
			remaining.forEach((item) => finalWidths.set(item.columnId, item.width));
			break;
		}
		const scale = available / total;
		const pinned = remaining.filter((item) => item.width * scale <= item.minWidth);
		const stillFlexible = remaining.filter((item) => item.width * scale > item.minWidth);

		if (pinned.length === 0) {
			stillFlexible.forEach((item) =>
				finalWidths.set(item.columnId, Math.round(item.width * scale))
			);
			break;
		}
		pinned.forEach((item) => finalWidths.set(item.columnId, item.minWidth));
		available -= pinned.reduce((sum, item) => sum + item.minWidth, 0);
		remaining = stillFlexible;
	}
	return finalWidths;
};

// Hands leftover space to whichever flexible columns can still take it,
// starting from the last (rightmost) one - a short row count or a table full
// of short values otherwise measures well under the container and leaves an
// empty gap at the right edge instead of the table filling its space the way
// auto layout used to. Respects each column's own maximum, spilling into the
// next column back if the last one is already capped.
const growToFit = (items, surplus) => {
	const finalWidths = new Map(items.map((item) => [item.columnId, item.width]));
	let remaining = surplus;
	for (let i = items.length - 1; i >= 0 && remaining > 0; i -= 1) {
		const item = items[i];
		const room = MAX_COLUMN_WIDTH_PX - item.width;
		if (room <= 0) continue;
		const grant = Math.min(room, remaining);
		finalWidths.set(item.columnId, item.width + grant);
		remaining -= grant;
	}
	return finalWidths;
};

// Columns are index-aligned with `columns` (see modelTaskColumns.js), which
// holds the field key each cell actually reads - not always the column id
// itself (the "pos" column sorts by "pos" but displays "orderNumber").
//
// The raw column def's own `id` is undefined until the table instance
// resolves it - `accessorKey` is the stable key that matches the id
// react-table assigns internally (and what ModelTaskHeader reads back off
// `header.column.columnDef`), so widths must be keyed by that instead.
//
// `containerWidth` is the scroll container's actual rendered width (already
// capped by screenSizeForTable for the current screen size and nav bar
// state) and `reservedWidth` is what pinned columns (logo, intervals, the
// selection checkbox) take out of it. Flexible columns are scaled down when
// their desired widths exceed what's left (only content that still can't
// fit at each column's minimum falls back to the table's own horizontal
// scroll), and grown to fill it when a short dataset needs less than the
// container has - either way the table matches its container instead of
// overflowing it or leaving a gap, the way auto layout used to most of the
// time.
export const computeColumnWidths = ({
	columnDefs,
	columns,
	data,
	containerWidth = 0,
	reservedWidth = 0,
}) => {
	const sample = data.slice(0, SAMPLE_SIZE);
	const desired = [];
	columnDefs.forEach((def, index) => {
		if (def.meta?.width) return;
		const columnId = def.accessorKey ?? def.id;
		const key = columns[index];
		const headerWidth = estimatedTextWidth(def.header, { bold: true });
		const cellWidth = sample.reduce(
			(max, row) => Math.max(max, estimatedTextWidth(row?.[key])),
			0
		);
		const isLastColumn = index === columnDefs.length - 1;
		const actionsAllowance = isLastColumn ? ACTIONS_BUTTON_WIDTH_PX : 0;
		const minWidth = MIN_COLUMN_WIDTH_PX + actionsAllowance;
		desired.push({
			columnId,
			width: clamp(Math.max(headerWidth, cellWidth) + CELL_PADDING_PX + actionsAllowance, minWidth),
			minWidth,
		});
	});

	const desiredTotal = desired.reduce((sum, column) => sum + column.width, 0);
	const available =
		containerWidth > 0
			? Math.max(containerWidth - reservedWidth - LAYOUT_SAFETY_MARGIN_PX, 0)
			: 0;
	// Shrinks to fit when content needs more than the container has, and
	// grows to fill it when content needs less - either way the table ends
	// up matching its container rather than overflowing it or leaving a gap.
	let finalWidths;
	if (available > 0 && desiredTotal > available) {
		finalWidths = fitToBudget(desired, available);
	} else if (available > 0 && desiredTotal < available) {
		finalWidths = growToFit(desired, available - desiredTotal);
	} else {
		finalWidths = new Map(desired.map((column) => [column.columnId, column.width]));
	}

	const widths = {};
	desired.forEach(({ columnId }) => {
		widths[columnId] = `${finalWidths.get(columnId)}px`;
	});
	return widths;
};

// What pinned columns (logo, intervals) plus the selection checkbox take out
// of the container before any flexible column gets a share. Matches the
// checkbox's own pinned width (see useModelTaskTableStyles) so the budget
// accounts for exactly what that column actually takes.
const useReservedWidth = (columnDefs, showSelection) =>
	useMemo(() => {
		const pinnedTotal = columnDefs.reduce(
			(sum, def) => sum + (parseInt(def.meta?.width, 10) || 0),
			0
		);
		return pinnedTotal + (showSelection ? SELECTION_COLUMN_WIDTH_PX : 0);
	}, [columnDefs, showSelection]);

// Sizes each column once from the first page of loaded data instead of
// leaving the browser's default auto table layout to keep recalculating
// column widths as the virtualiser recycles rows during scroll - that
// recalculation is what caused the mid-scroll flicker. Widths are locked in
// per column set and container width (which only changes on a real resize or
// nav bar toggle) so scrolling, sorting and paging never trigger a recompute.
//
// Returns columnDefs with the measured width merged into each one's
// meta.width, ready to hand straight to the table instance - the caller
// never needs the raw widths map itself.
const useAutoColumnWidths = ({ columnDefs, columns, data, containerWidth = 0, showSelection = false }) => {
	const reservedWidth = useReservedWidth(columnDefs, showSelection);
	const lockRef = useRef({ signature: null, widths: null });
	const signature = useMemo(
		() => columnDefs.map((def) => `${def.accessorKey ?? def.id}:${def.header}`).join("|"),
		[columnDefs]
	);
	const hasData = data.length > 0;
	const hasContainerWidth = containerWidth > 0;
	const lockKey = `${signature}@${containerWidth}@${reservedWidth}`;

	// Reads lockRef during render (safe - only writing to a ref during render
	// is the concurrent-rendering hazard, since an abandoned render could
	// leave a write behind that a discarded attempt never should have made).
	// The actual write happens below, in a layout effect, which React only
	// ever runs for a render that actually committed.
	const columnWidths = useMemo(() => {
		if (lockRef.current.signature === lockKey && lockRef.current.widths) {
			return lockRef.current.widths;
		}
		return computeColumnWidths({
			columnDefs,
			columns,
			data,
			containerWidth,
			reservedWidth,
		});
	}, [lockKey, columnDefs, columns, data, containerWidth, reservedWidth]);

	useLayoutEffect(() => {
		if (hasData && hasContainerWidth) lockRef.current = { signature: lockKey, widths: columnWidths };
	});

	return useMemo(
		() =>
			columnDefs.map((def) => {
				const width = columnWidths[def.accessorKey ?? def.id];
				return width ? { ...def, meta: { ...def.meta, width } } : def;
			}),
		[columnDefs, columnWidths]
	);
};

export default useAutoColumnWidths;
